import { ReactNode } from "react";

import LinkButton from "ds/components/Button/LinkButton";
import Feedback, { FeedbackProps } from "ds/components/Feedback";
import FeedbackActions from "ds/components/Feedback/Actions";
import { FeedbackType, FeedbackVariant } from "ds/components/Feedback/types";
import { AnalyticsPageGlobal } from "hooks/useAnalytics/pages/global";
import useTypedContext from "hooks/useTypedContext";
import { BillingTierFeature } from "types/generated";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";
import { AccountContext } from "views/AccountWrapper";

type TierInfoProps = {
  feature: BillingTierFeature | BillingTierFeature[];
  type?: FeedbackType;
  variant: FeedbackVariant;
  children: ReactNode;
  title?: string;
  className?: string;
  adminActionText?: string;
  notAdminTextPlaceholder?: string;
  storageKey?: string;
  margin?: FeedbackProps["margin"];
};

const TierInfo = ({
  feature,
  margin,
  title,
  children,
  className,
  type = "callout",
  variant = "danger",
  adminActionText = "Upgrade",
  notAdminTextPlaceholder = "Ask your admin to upgrade.",
  storageKey,
}: TierInfoProps) => {
  const { viewer } = useTypedContext(AccountContext);
  const { tier, tierFeatures } = useTypedContext(SubscriptionContext);

  const requiredFeatures = Array.isArray(feature) ? feature : [feature];
  const isActive = requiredFeatures.every((requiredFeature) =>
    tierFeatures.includes(requiredFeature)
  );

  if (isActive) return null;

  return (
    <Feedback
      title={title}
      variant={variant}
      className={className}
      type={type}
      storageKey={storageKey}
      margin={margin}
    >
      {children}
      <FeedbackActions>
        {viewer.admin ? (
          <LinkButton
            variant="contrast"
            size="small"
            to="/settings/billing"
            analyticsPage={AnalyticsPageGlobal.UpgradePlanBanner}
            analyticsTitle="Upgrade Button Clicked"
            analyticsProps={{
              buttonText: adminActionText,
              billingTierFeature: requiredFeatures.join(", "),
              currentTier: tier,
              variant,
              type,
            }}
          >
            {adminActionText}
          </LinkButton>
        ) : (
          notAdminTextPlaceholder
        )}
      </FeedbackActions>
    </Feedback>
  );
};

export default TierInfo;
